// =============================================================
// TaskCreateModal — the "New Task" / "Add Subtask" form, in one place.
//
// It was written out twice, in `pages/Tasks.jsx` and in `DealTasksPanel`, field
// for field: the same title/priority/status/due-date grid, the same subtask
// banner, the same two assignee pickers, the same "Enter to save" and the same
// disabled rule. The two copies had already begun to drift in their smaller
// details, and every future field would have had to be added to both — which is
// exactly how one surface ends up quietly missing a field the other has.
//
// THE ONE REAL DIFFERENCE IS THE SCOPE, and it is a prop: the Tasks page picks a
// deal, a deal's own panel already knows which deal it is on. Everything else
// the two surfaces do differently was incidental.
//
// It does not create tasks itself — `useTaskCreate` owns the post, the
// idempotency key and the near-duplicate gate, so those rules also live in one
// place rather than three.
// =============================================================

import { useState } from 'react';
import { isTaskGroup } from '@harvus/shared';
import { B } from '../../constants/brand';
import { Btn, Modal, Input, Select, Icon, useToast } from '../ui';
import { AssigneePicker } from '../shared/AssigneePicker';
import { DealScopePicker, FixedScopeProjectPicker } from '../shared/DealScopePicker';
import { InlineSearchPicker, EDITABLE_STATUSES, dealPickerValueFor } from './TaskComponents';
import { useTaskCreate } from './useTaskCreate';
import { todayIsoInZone } from '../../utils/format';

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

// The deal picker (components/shared/DealScopePicker.jsx) is one list of deals,
// their details AND their projects — a project is chosen in the same box as its
// deal/detail, not a second one. It is its own component so the catalog request
// is only made where the scope is actually chosen.
const todayISO = () => todayIsoInZone(); // the person's today, not UTC's

// A parent's own scope, as the picker's combo value.
const comboFor = (dealId, dealDetailId) => dealPickerValueFor({ dealId, dealDetailId });

export const TaskCreateModal = ({
  // Scope. `fixed` means the caller owns it (a deal's own panel) and no picker
  // is shown; otherwise the deal is chosen here.
  fixedScope = false,
  dealId: fixedDealId = '',
  dealDetailId: fixedDealDetailId = '',
  // The task this one is a subtask of, when opened from a task's "+" — and the
  // options for choosing one otherwise. Each option carries its own scope so a
  // subtask can inherit its parent's deal.
  parentTask = null,
  onClearParent,
  parentOptions = [],
  users = [],
  contacts = [],
  onClose,
  onCreated,
}) => {
  const { toast } = useToast();
  const { runTaskCreate, requestId, resetRequestId, duplicateDialog } = useTaskCreate();

  const seedScope = parentTask
    ? { dealId: parentTask.dealID || '', dealDetailId: parentTask.dealDetailID || '' }
    : { dealId: fixedDealId || '', dealDetailId: fixedDealDetailId || '' };

  const [title, setTitle]         = useState('');
  const [priority, setPriority]   = useState('Medium');
  const [status, setStatus]       = useState('Not Started');
  const [dueDate, setDueDate]     = useState(todayISO());
  const [parentTaskId, setParentTaskId] = useState(parentTask?.taskID || '');
  const [dealId, setDealId]       = useState(seedScope.dealId);
  const [dealDetailId, setDealDetailId] = useState(seedScope.dealDetailId);
  const [combo, setCombo]         = useState(comboFor(seedScope.dealId, seedScope.dealDetailId));
  const [projectId, setProjectId] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [outsideParties, setOutsideParties] = useState([]);
  const [saving, setSaving]       = useState(false);

  // One pick sets the deal, the detail and the project together.
  const setScope = (scope, value) => {
    setCombo(value || '');
    setDealId(scope.dealId || '');
    setDealDetailId(scope.dealDetailId || '');
    setProjectId(scope.projectId || '');
  };

  // A SUBTASK INHERITS ITS PARENT'S SCOPE. Every deal detail is selectable now,
  // so it inherits the detail as-is — this used to fall back to deal level
  // whenever the deal had one detail, because that detail had no row to pick.
  const setParent = (taskId) => {
    setParentTaskId(taskId || '');
    if (!taskId || fixedScope) return;
    const opt = parentOptions.find(o => o.value === taskId);
    setDealId(opt?.dealID || '');
    setDealDetailId(opt?.dealDetailID || '');
    setCombo(comboFor(opt?.dealID, opt?.dealDetailID));
    setProjectId('');
  };

  const create = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (assignees.length === 0) { toast.error('Assign the task to at least one person'); return; }
    setSaving(true);
    try {
      await runTaskCreate({
        title: title.trim(),
        priority,
        status,
        dueDate: dueDate || null,
        parentTaskId: parentTaskId || null,
        assigneeIds:        assignees.filter(a => a.type === 'user').map(a => a.id),
        contactAssigneeIds: assignees.filter(a => a.type === 'contact').map(a => a.id),
        outsidePartyIds:    outsideParties.map(a => a.id),
        dealId:       (fixedScope ? fixedDealId : dealId) || null,
        dealDetailId: (fixedScope ? fixedDealDetailId : dealDetailId) || null,
        projectId:    projectId || null,
        clientRequestId: requestId(),
      }, (created) => {
        resetRequestId();          // rotate so the next task gets a fresh key
        onCreated?.(created);
        onClose?.();
      });
    } catch (e) { toast.error(e.message); }   // keep the key so a manual retry dedupes
    finally { setSaving(false); }
  };

  return (
    <>
      <Modal title={parentTask ? `Add Subtask to: ${parentTask.title}` : 'New Task'} onClose={onClose} width={600}>
        {parentTask && (
          <div style={{ background: B.tealLt, border: `1px solid ${B.tealDk}`, borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: B.navyDk }}>
            <strong>Subtask of:</strong> {parentTask.title}
            {!isTaskGroup(parentTask) && (
              <div style={{ marginTop: 4, fontSize: 11, color: B.textMd }}>
                Its first subtask turns “{parentTask.title}” into a group heading: its own status, due date and assignees stop showing, and the subtasks are the work.
              </div>
            )}
            {onClearParent && (
              <button onClick={() => { setParentTaskId(''); onClearParent(); }} aria-label="Cancel subtask"
                style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: B.navyDk, display: 'inline-flex' }}>
                <Icon name="close" size={13} />
              </button>
            )}
          </div>
        )}

        <Input autoFocus label="Title" value={title} onChange={setTitle} required placeholder="Task title…"
          onKeyDown={e => { if (e.key === 'Enter' && title.trim()) create(); }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label="Priority" value={priority} onChange={setPriority} options={PRIORITIES} />
          <Select label="Status"   value={status}   onChange={setStatus}   options={EDITABLE_STATUSES.map(s => ({ value: s, label: s }))} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: parentTask ? '1fr' : '1fr 1fr', gap: 12 }}>
          <Input label="Due Date" type="date" value={dueDate} onChange={setDueDate} />
          {!parentTask && (
            <InlineSearchPicker label="Parent Task (optional)" value={parentTaskId} onChange={setParent}
              items={parentOptions} placeholder="Search tasks…" clearLabel="— None (top-level task) —" />
          )}
        </div>
        {!parentTask && parentTaskId && (
          <div style={{ marginTop: -6, marginBottom: 10, fontSize: 11, color: B.textMd }}>
            A task with subtasks becomes a group heading — its own status, due date and assignees stop showing, and the subtasks are the work.
          </div>
        )}

        {!fixedScope && <DealScopePicker value={combo} onChange={setScope} />}

        {/* On a deal's own panel the deal is fixed: the same list, narrowed to it. */}
        {fixedScope && fixedDealId && (
          <FixedScopeProjectPicker dealId={fixedDealId} dealDetailId={fixedDealDetailId}
            value={projectId} onChange={setProjectId} />
        )}

        <AssigneePicker selected={assignees} onChange={setAssignees} users={users} contacts={contacts} required />
        <AssigneePicker label="Outside Parties" searchPlaceholder="Search contacts to add…"
          selected={outsideParties} onChange={setOutsideParties} users={[]} contacts={contacts} />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: B.textLt, flex: 1 }}>Tip: Press Enter to save quickly</span>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn disabled={!title.trim() || assignees.length === 0 || saving} onClick={create}>
            {saving ? 'Creating…' : 'Create Task'}
          </Btn>
        </div>
      </Modal>
      {duplicateDialog}
    </>
  );
};
